import React, { useEffect, useState, ReactNode } from 'react';

import { AlignJustify, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import Sidebar from '../../components/layout/Sidebar';
import Topbar from '../../components/layout/Topbar';
import { getRoles, getPermissions } from '../../utils/tokenUtils';

interface Breadcrumb {
  label: string;
  href: string;
}

interface AdminLayoutProps {
  activeMenuItem: string;
  breadcrumbs: Breadcrumb[];
  allowedRoles: string[];
  children: ReactNode;
  requiredPermission?: string;
}

const AdminLayout: React.FC<AdminLayoutProps> = ({
  activeMenuItem,
  breadcrumbs,
  children,
  allowedRoles,
  requiredPermission
}) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [roles, setRoles] = useState<string[] | null>(null);
  const [permissions, setPermissions] = useState<string[] | null>(null);
  const [previousPath, setPreviousPath] = useState<string>('/login');

  const navigate = useNavigate();

  useEffect(() => {
    if (location.pathname !== '/login') {
      setPreviousPath(location.pathname);
    }
  }, [location]);

  useEffect(() => {
    const userRoles = getRoles();
    const userPermissions = getPermissions();
    setRoles(userRoles);
    setPermissions(userPermissions);
  }, []);

  useEffect(() => {
    if (roles && permissions) {
      const hasRole = roles.some((role) => allowedRoles.includes(role));
      const hasPermission = !requiredPermission || permissions.includes(requiredPermission);

      if (!hasRole || !hasPermission) {
        // Try to go back in history first
        if (window.history.length > 2) {
          navigate(-1);
        } else {
          // If no history, try the stored previous path
          if (previousPath && previousPath !== location.pathname) {
            navigate(previousPath);
          } else {
            // If all else fails, go to dashboard
            navigate('/login');
          }
        }
      }
    }
  }, [roles, permissions, allowedRoles, requiredPermission, navigate, previousPath, location.pathname]);

  const toggleSidebar = (): void => {
    setSidebarOpen(!sidebarOpen);
  };

  return (
    <div className="flex flex-col h-screen bg-bg">
      <Topbar />
      <div className="flex flex-1 overflow-hidden">
        <Sidebar isOpen={sidebarOpen} activePage={activeMenuItem} />
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Breadcrumb bar */}
          <div className="flex items-center h-11 min-h-11 px-6 gap-3 bg-panel border-b border-line">
            <button
              className="lg:hidden text-muted hover:text-text transition-colors"
              onClick={toggleSidebar}
            >
              <AlignJustify className="w-5 h-5" />
            </button>
            <nav>
              <ol className="flex items-center gap-0 list-none m-0 p-0">
                {breadcrumbs.map((bc, i) => (
                  <li key={i} className="flex items-center">
                    <a
                      href={bc.href}
                      className={`font-[Archivo] text-[13px] no-underline transition-colors ${
                        i === breadcrumbs.length - 1
                          ? 'font-semibold text-text'
                          : 'font-normal text-muted hover:text-text'
                      }`}
                    >
                      {bc.label}
                    </a>
                    {i < breadcrumbs.length - 1 && (
                      <ChevronRight className="w-3.5 h-3.5 text-muted opacity-35 mx-1" />
                    )}
                  </li>
                ))}
              </ol>
            </nav>
          </div>
          {/* Content area */}
          <div className="flex-1 overflow-auto cg-dot-grid" data-main-container>
            <div className="p-[22px]">
              {children}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminLayout;
